import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { TREASURY_KEY, treasuryApi, type TreasuryProvider } from '../../api/treasury.api';

import { ConnectionsLink } from './ConnectionsLink';
import { SettingsSectionHeader } from './SettingsSectionHeader';
import { TreasuryForm } from './TreasuryForm';
import { useCanWriteSection } from './useSettingsAccess';

/**
 * Settings -> Treasury -> Accounts (DEV-139): which account at each provider
 * holds customer money (GuruPay's client-money "master" account per
 * currency) and which account every customer of a shared-account provider is
 * given, with the name it is held under and the reference prefix. These sat
 * on the provider cards until S4 split them from the connection; they are
 * stored as versions of `treasury.<provider>` and the History reads them.
 *
 * A different client-money account needs a reason (the API requires it). A
 * changed shared account reaches the customer accounts already opened
 * against it.
 */

const INTRO: Readonly<Record<TreasuryProvider['provider'], string>> = {
  GURUPAY: 'The client-money account per currency is where customer money sits; every customer vIBAN is issued under it, and payouts are funded from it. The shared account fields apply when the shared-account mechanic is on.',
  BANKERA: 'Every Bankera customer is given this account and a reference of their own; payments in are told apart by the reference.',
  MANUAL: 'The bank account customers of the manual bank are given. Typed from the bank’s own statement; nothing here is checked against anybody.',
  IBANQ: 'Which of our IBANQ accounts holds customer money in each currency (one IBANQ account can hold several currencies), and where Norvix revenue goes. With the shared mechanic on, customers are given the client-money account and a reference of their own (holder name and prefix below). Payments out through IBANQ are a later phase.',
};

export function TreasuryAccountsPage(): JSX.Element {
  const treasury = useQuery({ queryKey: TREASURY_KEY, queryFn: () => treasuryApi.list() });
  // DEV-140: MLRO, Client manager and Finance read Treasury; Admin changes it.
  const readOnly = !useCanWriteSection({ group: 'treasury', id: 'accounts' });
  // A provider shows here once its connection exists or something is stored for it.
  const providers = (treasury.data ?? []).filter((p) => p.connection.exists || p.version > 0);

  return (
    <Box>
      <SettingsSectionHeader title="Accounts">
        Which accounts hold customer money, and which account each customer of a shared account is given. A
        different client-money account needs a reason. Connections and credentials are in{' '}
        <ConnectionsLink />.
      </SettingsSectionHeader>
      {treasury.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : treasury.isError ? (
        <Alert severity="error">Could not load the treasury settings.</Alert>
      ) : providers.length === 0 ? (
        <Alert severity="info">
          No provider holds accounts for us yet. Set one up first in <ConnectionsLink />.
        </Alert>
      ) : (
        providers.map((provider) => (
          <TreasuryForm
            key={provider.provider}
            provider={provider}
            section="TREASURY_ACCOUNTS"
            historyTitle="Accounts"
            readOnly={readOnly}
            // The manual bank has no API to ask.
            {...(provider.provider === 'MANUAL' ? {} : {
              discovery: {
                fill: true,
                // The client-money row offers the master accounts; a shared account any account the partner lists.
                offer: (accounts, field) => (field.key === 'masterAccountUuids' ? accounts.filter((a) => a.isMaster) : accounts),
              },
            })}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{INTRO[provider.provider]}</Typography>
            {!provider.connection.configured && provider.provider !== 'MANUAL' ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                The {provider.vendor} connection is incomplete (still needed: {provider.connection.missingFields.join(', ')}), so its
                accounts cannot be listed. Complete it in <ConnectionsLink />.
              </Alert>
            ) : null}
          </TreasuryForm>
        ))
      )}
    </Box>
  );
}
